import type { LanguageCode } from '@alltrails/types/LanguageCode';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { languageRegionCodes } from '@alltrails/types/LanguageRegionCode';
import type { Locale } from '@alltrails/types/Locale';
import { locales } from '@alltrails/types/Locale';

const LANGUAGE_REGION_CODE_TO_LOCALE: Record<LanguageRegionCode, Locale> = {
  'da-DK': 'da-dk',
  'nl-NL': 'nl-nl',
  'en-US': 'en',
  'en-GB': 'en-gb',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'it-IT': 'it-it',
  'nb-NO': 'nb-no',
  'pl-PL': 'pl-pl',
  'pt-PT': 'pt-pt',
  'pt-BR': 'pt-br',
  'es-ES': 'es',
  'es-MX': 'es-mx',
  'sv-SE': 'sv-se'
};

const LOCALE_TO_LANGUAGE_REGION_CODE: Record<Locale, LanguageRegionCode> = {
  'da-dk': 'da-DK',
  'nl-nl': 'nl-NL',
  en: 'en-US',
  'en-gb': 'en-GB',
  fr: 'fr-FR',
  de: 'de-DE',
  'it-it': 'it-IT',
  'nb-no': 'nb-NO',
  'pl-pl': 'pl-PL',
  'pt-pt': 'pt-PT',
  'pt-br': 'pt-BR',
  es: 'es-ES',
  'es-mx': 'es-MX',
  'sv-se': 'sv-SE'
};

/**
 * Type guard to ensure all LanguageRegionCode values are mapped.
 */
function assertCompleteLanguageRegionCodeMapping(): void {
  const unmapped = languageRegionCodes.filter(code => !(code in LANGUAGE_REGION_CODE_TO_LOCALE));
  if (unmapped.length > 0) {
    throw new Error(`Missing LanguageRegionCode to Locale mapping for: ${unmapped.join(', ')}`);
  }
}

/**
 * Type guard to ensure all Locale values are mapped.
 */
function assertCompleteLocaleMapping(): void {
  const unmapped = locales.filter(locale => !(locale in LOCALE_TO_LANGUAGE_REGION_CODE));
  if (unmapped.length > 0) {
    throw new Error(`Missing Locale to LanguageRegionCode mapping for: ${unmapped.join(', ')}`);
  }
}

// Assert mappings are complete at module load time
assertCompleteLanguageRegionCodeMapping();
assertCompleteLocaleMapping();

export function getLanguageCodeFromLanguageRegionCode(languageRegionCode: LanguageRegionCode): LanguageCode {
  return languageRegionCode.split('-')[0] as LanguageCode;
}

export function getLocaleFromLanguageRegionCode(languageRegionCode: LanguageRegionCode): Locale {
  return LANGUAGE_REGION_CODE_TO_LOCALE[languageRegionCode];
}

export function getLocalePrefixFromLanguageRegionCode(languageRegionCode: LanguageRegionCode): Locale | string {
  if (languageRegionCode === 'en-US') {
    return '';
  }

  return getLocaleFromLanguageRegionCode(languageRegionCode);
}

export function getLanguageRegionCodeFromLocale(locale: Locale): LanguageRegionCode {
  return LOCALE_TO_LANGUAGE_REGION_CODE[locale];
}
